import { Box, Grid, Stack, SxProps, Theme, useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useLayoutEffect, useRef, useState } from 'react';

import { FooterContinueButton } from '@components';
import { useStore } from '@stores';
import { globalTheme as theme } from '@styles';
import { FOOTER_DIV_ID } from '@utils';

import { FooterLinks } from './FooterLinks';

const footerStyles = {
  footerContainer: {
    '@media (min-width:961px)': {
      position: 'fixed',
      zIndex: 99,
      bottom: 0,
      left: 0,
      boxSizing: 'border-box',
      background: 'white',
      width: '100%',
    },
    borderTop: '1px solid #e8e8e8',
    '@media (max-width:960px)': {
      padding: '15px',
      boxShadow: '0 -4px 8px 0 rgba(0, 0, 0, 0.08)',
      borderRadius: '16px 16px 0 0',
      position: 'fixed',
      marginTop: '10vh',
      bottom: 0,
      boxSizing: 'border-box',
      left: 0,
      width: '100vw',
      backgroundColor: theme.palette.brandGray.lighter,
    },
  },
  noBorderFooterContainer: {
    padding: '15px',
    '@media (max-width:960px)': {
      padding: '24px',
    },
  },
  gridContainer: {
    alignItems: 'center',
    display: 'flex',
    '@media (max-width:960px)': {
      flexDirection: 'column',
      textAlign: 'center',
      gap: '18px',
    },
  },
  leftElement: {
    '@media (max-width: 960px)': {
      order: 2,
    },
  },
  rightElement: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row-reverse',
    '@media (max-width: 960px)': {
      flexDirection: 'column',
      order: 1,
      width: '100%',
      justifyContent: 'center',
    },
  },
  footerNoteGrid: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

interface ModalFooterProps {
  onSubmit?: () => void;
  isDisabled?: boolean;
  hideBorder?: boolean;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const ModalFooter = observer(
  ({
    onSubmit,
    isDisabled,
    hideBorder = false,
    children,
    sx,
  }: ModalFooterProps) => {
    const { formStore } = useStore();
    const hasConsent = formStore.getSubmodule?.some(
      (name) => name === 'SUB_CONTACT' || name === 'SUB_COMPACT_CONTACT'
    );
    const footerRef = useRef<HTMLDivElement>(null);
    const [footerHeight, setFooterHeight] = useState(0);
    useLayoutEffect(() => {
      if (!hasConsent || !footerRef.current) return;
      const footer = footerRef.current;
      const updateHeight = () =>
        setFooterHeight(footer.getBoundingClientRect().height);
      updateHeight();
      const observer = new ResizeObserver(updateHeight);
      observer.observe(footer);
      return () => observer.disconnect();
    }, [hasConsent]);
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    return (
      <>
        <Box
          ref={footerRef}
          sx={[
            hideBorder && !isDesktop && !hasConsent
              ? footerStyles.noBorderFooterContainer
              : footerStyles.footerContainer,
            hasConsent &&
              !isDesktop && {
                position: 'fixed',
                zIndex: 99,
                marginTop: 0,
                '@media (max-width:960px)': {
                  marginTop: 0,
                  borderRadius: 0,
                  boxShadow: 'none',
                  backgroundColor: 'background.paper',
                },
                backgroundColor: 'background.paper',
                borderTop: '1px solid',
                borderColor: 'brandGray.light',
                px: { xs: 2, md: 0 },
                pt: { xs: 1.5, md: 0 },
                pb: {
                  xs: 'calc(12px + env(safe-area-inset-bottom, 0px))',
                  md: 0,
                },
              },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          id={FOOTER_DIV_ID}
        >
          <Box
            sx={{
              [theme.breakpoints.up('md')]: {
                padding: '14px 20px',
                display: 'flex',
                flexDirection: 'row',
                maxHeight: '74px',
                justifyContent: 'flex-end',
                gap: { md: '40px' },
                alignItems: 'center',
              },
              [theme.breakpoints.down('md')]: {
                marginTop: hasConsent ? 0 : '-30px',
              },
            }}
          >
            <Grid container sx={footerStyles.gridContainer}>
              {!isDesktop && !hasConsent && (
                <Grid item xs={12} md="auto" sx={footerStyles.leftElement}>
                  <Stack direction={'column'} alignItems={'center'} spacing={1}>
                    <FooterLinks />
                  </Stack>
                </Grid>
              )}

              <Grid
                item
                xs={12}
                md="auto"
                sx={{
                  ...footerStyles.rightElement,
                  ml: 'auto',
                }}
              >
                {children ? (
                  children
                ) : (
                  <FooterContinueButton
                    onSubmit={onSubmit}
                    isDisabled={isDisabled}
                  />
                )}
              </Grid>
            </Grid>
          </Box>

          {isDesktop && (
            <Box
              sx={{
                borderTop: '1px solid #e8e8e8',
                left: 0,
                boxSizing: 'border-box',
                background: 'white',
                width: '100%',
                flexGrow: 1,
                display: 'flex',
                minHeight: '36px',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Stack
                direction={'row'}
                alignItems={'center'}
                spacing={1}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <FooterLinks />
              </Stack>
            </Box>
          )}
        </Box>
        {hasConsent && !isDesktop && (
          <Box sx={{ px: 3, py: 2, textAlign: 'center' }}>
            <FooterLinks />
          </Box>
        )}
        {hasConsent && (
          <Box
            aria-hidden
            sx={{
              display: { xs: 'block', md: 'none' },
              height: footerHeight + 24,
            }}
          />
        )}
      </>
    );
  }
);
